// CourierDashboard.tsx
import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { MetricCard } from "./components/courier-dashboard/MetricCard";
import { FilterBar } from "./components/courier-dashboard/FilterBar";
import type { FilterValues } from "./components/courier-dashboard/types";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./components/ui/accordion";
import { useApiServices } from "./hooks/useApiServices";
import { API_ENDPOINTS } from "./lib/types";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// Helpers de formateo para evitar "undefined%"
const pct = (v?: number) => (typeof v === "number" ? v.toFixed(2) + "%" : "—");
const dec = (v?: number) => (typeof v === "number" ? v.toFixed(2) : "—");

const infoHelp = {
  deliveryRate:
    "Porcentaje de órdenes entregadas sobre el total de órdenes.",
  firstAttempt:
    "Porcentaje de órdenes entregadas que se lograron en la primera visita (sin reintentos).",
  onTime:
    "Porcentaje de órdenes entregadas dentro del compromiso de tiempo, sobre todas las entregadas.",
  visitCompliance:
    "Porcentaje de órdenes con al menos una visita a tiempo (incluye entregas a tiempo), sobre entregadas o con intentos.",
  minmax:
    "Mínimo y máximo número de visitas registradas antes de la entrega.",
  avg:
    "Promedio de visitas registradas entre las órdenes que tuvieron al menos una visita."
};

const last30 = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - 30);
  return { from: from.toISOString(), to: to.toISOString() };
};

const CourierDashboard = () => {
  // Estado de filtros
  const [filters, setFilters] = useState<FilterValues>({
    logisticOperators: [],
    couriers: [],
    shippingType: [],
    deliveryType: [],
    regions: [],
    communes: [],
    timeRange: last30(),
  });

  const { fetchCourierService } = useApiServices();

  // Ref para imprimir TODO (cards + listado)
  const printRef = useRef<HTMLDivElement>(null);

  // Carga de datos
  const { data, isLoading } = useQuery({
    queryKey: [API_ENDPOINTS.courier_service, filters],
    queryFn: () => fetchCourierService(filters),
  });

  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">Cargando...</div>;
  }
  if (!data) {
    return <div className="flex items-center justify-center min-h-screen">No hay datos disponibles</div>;
  }

  // Estructura tal cual venía
  const globalStats = data.globalStatistics;
  const couriers = data.couriers;

  // Exportar PDF multipágina del contenedor completo
  const handleExportPDF = async () => {
    const node = printRef.current;
    if (!node) return;

    const canvas = await html2canvas(node, { scale: window.devicePixelRatio || 2, useCORS: true });
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 24;

    const imgW = pageW - margin * 2;
    const imgH = (canvas.height * imgW) / canvas.width;

    pdf.setFontSize(16);
    pdf.text("KPIs de Entrega", margin, margin);

    const startY = margin + 12;
    const printableH = pageH - startY - margin;

    if (imgH <= printableH) {
      pdf.addImage(imgData, "PNG", margin, startY, imgW, imgH);
    } else {
      // Slicing vertical
      let sY = 0;
      const sliceH = Math.floor(canvas.height * (printableH / imgH));

      while (sY < canvas.height) {
        const h = Math.min(sliceH, canvas.height - sY);
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = h;
        const ctx = sliceCanvas.getContext("2d")!;
        ctx.drawImage(canvas, 0, sY, canvas.width, h, 0, 0, canvas.width, h);

        const sliceData = sliceCanvas.toDataURL("image/png");
        pdf.addImage(sliceData, "PNG", margin, startY, imgW, (h * imgW) / canvas.width);

        sY += h;
        if (sY < canvas.height) {
          pdf.addPage();
          pdf.text("KPIs de Entrega", margin, margin);
        }
      }
    }

    pdf.save("metricas-entrega.pdf");
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Filtros y botón en la MISMA fila */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[320px]">
          <FilterBar filters={filters} onFilterChange={setFilters} />
        </div>
        <div className="shrink-0 self-start">
          <button
            onClick={handleExportPDF}
            className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800"
          >
            Descargar PDF
          </button>
        </div>
      </div>

      {/* Contenido a imprimir (cards + accordions) */}
      <div ref={printRef}>
        {/* Métricas globales */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-6 gap-5">
          <MetricCard
            title="Efectividad Global"
            value={pct(globalStats?.deliveryRate)}
            info={infoHelp.deliveryRate}
          />
          <MetricCard
            title="Efectividad en Primera Visita"
            value={pct(globalStats?.firstAttemptDeliveryRate)}
            info={infoHelp.firstAttempt}
          />
          <MetricCard
            title="Cumplimiento Entrega"
            value={pct(globalStats?.onTimeDeliveryRate)}
            info={infoHelp.onTime}
          />
          <MetricCard
            title="Cumplimiento Agenda"
            value={pct(globalStats?.cumplimientoVisitaRate)}
            info={infoHelp.visitCompliance}
          />
          <MetricCard
            title="Min/Max Visitas"
            value={`${globalStats?.minDeliveryAttempts ?? "—"} - ${globalStats?.maxDeliveryAttempts ?? "—"}`}
            info={infoHelp.minmax}
          />
          <MetricCard
            title="Promedio de Visitas"
            value={dec(globalStats?.avgDeliveryAttempts)}
            info={infoHelp.avg}
          />
        </div>

        {/* Lista de couriers */}
        <Accordion type="single" collapsible className="mt-6">
          {couriers.map((courier: any) => (
            <AccordionItem key={courier.id} value={courier.id}>
              <AccordionTrigger>
                <div className="w-full text-left">
                  <div className="text-lg font-semibold mb-2">{courier.id}</div>
                  <div className="grid grid-cols-1 md:grid-cols-6 gap-5 text-sm">
                    <div>{pct(courier?.deliveryRate)} Efectividad Global</div>
                    <div>{pct(courier?.firstAttemptDeliveryRate)} 1ra Visita</div>
                    <div>{pct(courier?.onTimeDeliveryRate)} Cumpl. Agenda</div>
                    <div>{pct(courier?.cumplimientoVisitaRate)} Cumpl. Visita</div>
                    <div>{courier?.minDeliveryAttempts ?? "—"}-{courier?.maxDeliveryAttempts ?? "—"} Min/Max</div>
                    <div>{dec(courier?.avgDeliveryAttempts)} Prom. Visitas</div>
                  </div>
                </div>
              </AccordionTrigger>

              {/* Detalle por región y comuna */}
              <AccordionContent>
                <div className="space-y-6">
                  <Accordion type="single" collapsible>
                    {/* Región */}
                    <AccordionItem value="region">
                      <AccordionTrigger>Por Región</AccordionTrigger>
                      <AccordionContent>
                        {courier.regions.map((region: any) => (
                          <div key={region.id} className="mb-4">
                            <div className="font-medium">{region.id}</div>
                            <div className="grid grid-cols-1 md:grid-cols-6 gap-5 text-sm text-muted-foreground">
                              <div>Efectividad Global: {pct(region?.deliveryRate)}</div>
                              <div>1ra Visita: {pct(region?.firstAttemptDeliveryRate)}</div>
                              <div>Cumpl. Agenda: {pct(region?.onTimeDeliveryRate)}</div>
                              <div>Cumpl. Visita: {pct(region?.cumplimientoVisitaRate)}</div>
                              <div>Min/Max: {region?.minDeliveryAttempts ?? "—"} - {region?.maxDeliveryAttempts ?? "—"}</div>
                              <div>Prom. Visitas: {dec(region?.avgDeliveryAttempts)}</div>
                            </div>
                          </div>
                        ))}
                      </AccordionContent>
                    </AccordionItem>

                    {/* Comuna */}
                    <AccordionItem value="commune">
                      <AccordionTrigger>Por Comuna</AccordionTrigger>
                      <AccordionContent>
                        {courier.communes.map((commune: any) => (
                          <div key={commune.id} className="mb-4">
                            <div className="font-medium">{commune.id}</div>
                            <div className="grid grid-cols-1 md:grid-cols-6 gap-5 text-sm text-muted-foreground">
                              <div>Efect. Global: {pct(commune?.deliveryRate)}</div>
                              <div>1ra Visita: {pct(commune?.firstAttemptDeliveryRate)}</div>
                              <div>Agenda: {pct(commune?.onTimeDeliveryRate)}</div>
                              <div>Visita: {pct(commune?.cumplimientoVisitaRate)}</div>
                              <div>Min/Max: {commune?.minDeliveryAttempts ?? "—"} - {commune?.maxDeliveryAttempts ?? "—"}</div>
                              <div>Prom.: {dec(commune?.avgDeliveryAttempts)}</div>
                            </div>
                          </div>
                        ))}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
};

export default CourierDashboard;